import _defineProperty from "@babel/runtime/helpers/defineProperty";
/*
 *                     NOTICE
 *            COPYRIGHT 2020 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import * as AbReactResources from "abreact/generated/locale/AbReactResources";
import { AbLogin } from "abreact/incubator/ui/login/AbLogin";
import { PopupUtils } from "abreact/incubator/ui/popup/PopupUtils";
import { AbButtonType } from "absdk/commons/ui/misc/AbButtonType";
import { AbAlert, AlertLevel } from "absdk/commons/ui/popup/AbAlert";
import { AbDialog } from "absdk/commons/ui/popup/AbDialog";
import { AbWebServices } from "abweb/service/AbWebServices";
import { LostSessionErrorType } from "abweb/service/authentication/IAuthenticationService";
import { ProxyRPCService } from "abweb/service/rpc/ProxyRPCService";
import { Assert } from "abweb/utils/Assert";
import { LogUtils } from "abweb/utils/log/LogUtils";
import { ManualPromise } from "abweb/utils/PromiseUtils";
import * as React from "react";
const LOG = LogUtils.getLogger(__filename);
// See https://confluence.abinitio.com/display/~bmiller/Spec+for+Client+Handling+of+Authentication+Errors
// and corresponding EI-15366 for spec describing desired behavior for stateful servers
/**
 * Handles case where server holds user-specific content that must be in sync with the current client state.
 * If a user needs to re-login as current session is invalid, the server-side user session state is lost,
 * and thus the client also needs to reload so as to be in sync with new server-side user state.
 * Interacts with the user via react-based dialogs.
 */
export class StatefulServerAuthenticationStrategy {
  constructor() {
    // Some of the dialogs have very long messages with causes dialog to be as wide as entire browser, with only a
    // couple of lines of text (so very wide and very short).  Use fixed width for the dialog to ensure reasonable size.
    _defineProperty(this, "dlgWidth", "800px");
    /** A latch that ensure that dialog is only showed once even though multiple threads (rpcs) may be failing with
     * authentication errors.  After dialog is finished, the 'unlatch()' will be invoked to release all threads
     * awaiting the current dialog results.
     */
    _defineProperty(this, "currentLatch", null);
    _defineProperty(this, "currentInitialErrorContext", null);
    ///////  Actions //////////
    /**
     * If invoked from dialog, should set primary/secondaryBtnCloseDialog=false as while dialog displayed the original UI is obscured,
     * so this avoids original UI from peeking through (see ABSDK-3405)
     */
    _defineProperty(this, "onLogoutThenLogin", async () => {
      // loginViaBrowserRefresh promise never resolves
      await AbWebServices.authenticationService.loginViaBrowserRefresh(true);
      // never reach here, so of course the latch is never released
      // so logout (invoked via browser tab refresh) happens while all queued up threads are waiting for currentLatch to resolve (never)
    });
    /**
     * login directly - should only be called when user is already logged out (invalid session) or want to keep existing (stolen) session.
     * If invoked from dialog, should set primary/secondaryBtnCloseDialog=false as while dialog displayed the original UI is obscured,
     * so this avoids original UI from peeking through (see ABSDK-3405)
     */
    _defineProperty(this, "onLogin", async () => {
      // loginViaBrowserRefresh promise never resolves
      await AbWebServices.authenticationService.loginViaBrowserRefresh(false); // logout never returns
      // never reach here, so of course the latch is never released
      // so logout (invoked via browser tab refresh) happens while all queued up threads are waiting for currentLatch to resolve (never)
    });
    _defineProperty(this, "onReenableConcurrentSession", async () => {
      // Note below reenableConcurrentSession will fail if invalid session (but session should be disabled).
      // If invalid session, the rpc service will delegate back to this class and await the current latch (thus browser is hung)
      // If it turns out that this is a valid case we have to worry about, re-implement reenableConcurrentSession to
      // bypass rpcservice 'authenticationError' handling that initiates this workflow (probably by making fetch-based)
      await AbWebServices.authenticationService.reenableConcurrentSession();

      // successfully reactivated session, so release all 'awaiting rpcs' so that they can retry their rpcs
      this.releaseLatch();
    });
  }
  /** @inheritDoc */
  handleLostSession(errCtx) {
    // Tricky: this entire method is really a synchronous method that happens to return a Promise<void>.
    // There are NO internal awaits, which makes in non-interruptible (aka atomic) from a javascript perspective.

    // Defensive: Will log warning if already latched, but this caller has a different errCtx than first latched caller
    let latch = this.alreadyLatched(errCtx);
    if (!latch) {
      // lazily create initial latch for first caller
      latch = this.createLatch(errCtx);

      // Sync on purpose as want all callers to await the latch promise, and creating/reusing latch is sync
      this.lostSessionErrorDispatcher(errCtx);
    }
    return latch.promise;
  }
  lostSessionErrorDispatcher(errCtx)
  // Do NOT make async!
  {
    // Simply delegates (MUST BE A SYNCHRONOUS METHOD).
    // Up to handle* method to invoke releaseLatch via their action handlers (via on* methods below)
    // Do NOT await the handle* methods, as caller(s) waits on the latch.  Workflows that support
    // retrying the failed rpc will release the latch (otherwise everything remains latched and the
    // browser tab is reloaded).

    // Stop singleton polling AND set its clientSessionId to null (which triggers unsubscribe for all current
    // useResubscribeStreamingApiHook users).  After relogin/reconnect, setting new clientSessionId will trigger
    // subsequent useResubscribeStreamingApiHook resubscribes.
    AbWebServices.getRPCService().closeClientSession();

    // Ensure any directProxies call explicit logout, otherwise the direct proxy jessionid cookie is still valid,
    // and will be reused on subsequent relogin (PORTAL-437)
    ProxyRPCService.reset();
    switch (errCtx.lostSessionErrorType) {
      case LostSessionErrorType.SESSION_INVALID:
        // No valid jsessionid.  No recovery possible for stateful, so onLogin.
        // Stateless can recover via
        // a) sso: reconnect after logging in via another tab,   BUG: ABSDK-4526 check reconnect user the same
        // b) non-sso: ajax relogin  (invokes onLogin if they login as different user).
        this.handleSessionInvalid(errCtx);
        break;
      case LostSessionErrorType.CONCURRENT_SESSION_INVALID:
        // This case is when server has detected concurrent logins, AND server HAS invalidated current jsessionid session.
        // No recovery is possible, except stateless non-sso via ajax relogin (no browser tab reload!)
        this.handleConcurrentSessionInvalid(errCtx);
        break;
      case LostSessionErrorType.CONCURRENT_SESSION_DISABLED:
        // This case is when server has detected concurrent logins, but has NOT invalidated current jsessionid session.
        // stateful/stateless same.  sso: reenableConcurrentSession, non-sso: reenableConcurrentSession || onLogoutThenLogin
        this.handleConcurrentSessionDisabled(errCtx);
        break;
      case LostSessionErrorType.BROWSER_TAB_STOLEN_SESSION:
        // has valid new jsessionid as someone logged in via another browser tab
        // stateless: sso and non-sso just refresh via onlogin (without logout) browser tab refresh.  No login prompt as user already logged in
        // stateful: sso and non-sso just invoke clientSessionService.reinitializeSession() for current new jsessionid
        // noinspection JSIgnoredPromiseFromCall
        this.handleStolenSession(errCtx);
        break;
      default:
        // Trick that guarantees case statement for every LostSessionErrorType enum value at compile time
        let x = errCtx.lostSessionErrorType;
        throw new Error("Unknown LostSessionErrorContext: '" + x + "'");
      // not reachable
    }
  }

  //////   Latch Methods //////

  /** Return current latch iff already latched */
  alreadyLatched(errCtx) {
    if (this.currentLatch == null) {
      return null;
    }

    // There is an implicit assumption that all rpcs are failing with the same type of LostSessionErrorType,
    // which should be true (e.g. not sure how would handle one rpc failing with a session_invalid and
    // another failing with concurrent_session_invalid --> which should be impossible)
    // Defensively log if it happens
    if (this.currentInitialErrorContext?.lostSessionErrorType !== errCtx.lostSessionErrorType) {
      LOG.warn(`Unexpected subsequent lostSessionErrorType '${errCtx.lostSessionErrorType}' ` + ` when initial lostSessionErrorType is '${this.currentInitialErrorContext?.lostSessionErrorType}'`);
    }
    return this.currentLatch;
  }
  createLatch(errCtx) {
    this.currentInitialErrorContext = errCtx;
    return this.currentLatch = new ManualPromise();
  }
  releaseLatch() {
    // All threads awaiting current promise (created by first handleLostSession invocation) are released.
    if (this.currentLatch) {
      this.currentLatch.resolve(); // releases all awaiting rpcs
      this.currentInitialErrorContext = null;
      this.currentLatch = null;
    }
  }

  //////   Authentication Strategy Error Handler Methods //////

  handleSessionInvalid(errCtx) {
    // Same for both sso and not sso
    this.showActionDialog(AbReactResources.authenticationStrategy_invalidSession_title(), AbReactResources.authenticationStrategy_stateful_invalidSession_message(), AbReactResources.loginDialog_logIn(), this.onLogin, false);
  }
  handleConcurrentSessionInvalid(errCtx) {
    errCtx.sso ? this.showActionDialog(AbReactResources.authenticationStrategy_invalidSession_title(), AbReactResources.authenticationStrategy_stateful_concurrentInvalidSession_sso_message(this.currentUsername), AbReactResources.loginDialog_logIn(), this.onLogin, false) : this.showActionDialog(AbReactResources.authenticationStrategy_invalidSession_title(), AbReactResources.authenticationStrategy_stateful_concurrentInvalidSession_notSso_message(this.currentUsername), AbReactResources.authenticationStrategy_actionButton_loginOK_title(), this.onLogin, false);
  }
  handleConcurrentSessionDisabled(errCtx) {
    errCtx.sso ? this.showActionDialog(AbReactResources.authenticationStrategy_stateful_disabledSession_title(), AbReactResources.authenticationStrategy_stateful_concurrentDisabledSession_sso_message(this.currentUsername), AbReactResources.authenticationStrategy_actionButton_reactivate_title(), this.onReenableConcurrentSession, true) : this.showActionDialog(AbReactResources.authenticationStrategy_stateful_disabledSession_title(), AbReactResources.authenticationStrategy_stateful_concurrentDisabledSession_notSso_message(this.currentUsername), AbReactResources.authenticationStrategy_actionButton_reactivate_title(), this.onReenableConcurrentSession, true, AbReactResources.authenticationStrategy_actionButton_logout_title(), this.onLogoutThenLogin, false);
  }
  async handleStolenSession(errCtx) {
    // Note as user has valid (stolen) session, onLogin (which does not logout) essentially just refreshes browser tab
    this.showActionDialog(AbReactResources.authenticationStrategy_invalidSession_title(), AbReactResources.authenticationStrategy_stateful_stolenSession_message(), AbReactResources.authenticationStrategy_actionButton_refresh_title(), this.onLogin, false);
  }
  ///////// Dialog Helpers //////////

  /** Show action dialog with a primary button and action, and optional secondary button and action */
  showActionDialog(title, msg, primaryBtnTitle, primaryBtnAction, primaryBtnCloseDialog = true, secondaryBtnTitle, secondaryBtnAction, secondaryBtnCloseDialog = true) {
    // Tried a button bar approach (<AbDialogButtonBar), but could not figure out how to get a totally custom button
    // on the button bar to close the dialog.
    // So used 'builtin' ok and cancel as primary/secondary buttons, which works well in practice.

    const dlgProps = {
      title,
      id: StatefulServerAuthenticationStrategy.AUTH_STRATEGY_ACTION_DLG_ID,
      className: "ab-auth-strategy-dialog ab-overlay-class-portal",
      /* PORTAL-1089: make background be same background as portal login page */
      style: {
        maxWidth: this.dlgWidth
      },
      closeButton: false,
      resizable: false,
      okButton: primaryBtnTitle,
      okButtonProps: {
        type: AbButtonType.PRIMARY
      },
      onOk: async () => {
        primaryBtnAction();
        return primaryBtnCloseDialog;
      }
    };
    if (secondaryBtnTitle) {
      Assert.assert(secondaryBtnAction != null, "secondaryBtnAction needs to be defined if secondaryBtnTitle is defined");
      dlgProps.cancelButton = secondaryBtnTitle;
      dlgProps.onCancel = async () => {
        secondaryBtnAction();
        return secondaryBtnCloseDialog;
      };
    }
    const popup = /*#__PURE__*/React.createElement(AbDialog, dlgProps, /*#__PURE__*/React.createElement(AbAlert, {
      className: "ab-login-dialog-error-message",
      level: AlertLevel.ERROR,
      message: msg
    }));

    // For RTD apps, this will auto-close the popup on major route change (user hits back button while popup open),
    // which prevents popup leaks from one view to another
    const popupHandle = PopupUtils.showPopupExplicitly(popup, {
      layerGroupId: AbLogin.LOGIN_LAYER_GROUP_ID
    });
    return popupHandle.untilClosed;
  }

  /////// Misc ///////

  get currentUsername() {
    return AbWebServices.sessionData.currentUser?.username;
  }
}
_defineProperty(StatefulServerAuthenticationStrategy, "AUTH_STRATEGY_ACTION_DLG_ID", "ab-auth-strategy-dialog");
//# sourceMappingURL=StatefulServerAuthenticationStrategy.js.map
